import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  getReportSummary, getTransactionReport,
  getLowStockReport, getValueByTypeReport,
} from '../lib/api';
import PageHeader from '../components/layout/PageHeader';

type Tab = 'summary' | 'transactions' | 'low-stock' | 'value-by-type';

export default function Reports() {
  const [tab, setTab] = useState<Tab>('summary');
  const [txFilter, setTxFilter] = useState({ start: '', end: '', type: '', user: '' });
  const [txPage, setTxPage] = useState(1);

  const tabs: { id: Tab; label: string }[] = [
    { id: 'summary',      label: 'Summary' },
    { id: 'transactions', label: 'Transaction Log' },
    { id: 'low-stock',    label: 'Low Stock' },
    { id: 'value-by-type',label: 'Value by Type' },
  ];

  return (
    <div className="h-full flex flex-col">
      <PageHeader
        title="Reports"
        breadcrumb={[{ label: 'Dashboard', href: '/dashboard' }, { label: 'Reports' }]}
      />

      {/* Tabs */}
      <div className="border-b border-gray-200 bg-white px-6 flex gap-1">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              tab === t.id
                ? 'border-brand-600 text-brand-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        {tab === 'summary'       && <SummaryTab />}
        {tab === 'transactions'  && (
          <TransactionsTab
            filter={txFilter} onFilterChange={setTxFilter}
            page={txPage} onPageChange={setTxPage}
          />
        )}
        {tab === 'low-stock'     && <LowStockTab />}
        {tab === 'value-by-type' && <ValueByTypeTab />}
      </div>
    </div>
  );
}

function SummaryTab() {
  const { data } = useQuery({ queryKey: ['report-summary'], queryFn: getReportSummary });
  return (
    <div>
      <div className="grid grid-cols-3 gap-4 mb-8">
        <StatCard label="Total Parts" value={data?.total_items ?? '—'} />
        <StatCard label="Total Inventory Value" value={data?.total_value != null ? `$${data.total_value.toFixed(2)}` : '—'} />
        <StatCard label="Low Stock Items" value={data?.low_stock_count ?? '—'} color="text-yellow-600" />
      </div>
      {data?.recent_activity && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Recent Activity</h3>
          <ActivityTable rows={data.recent_activity} />
        </div>
      )}
    </div>
  );
}

function TransactionsTab({
  filter, onFilterChange, page, onPageChange,
}: {
  filter: { start: string; end: string; type: string; user: string };
  onFilterChange: (f: typeof filter) => void;
  page: number;
  onPageChange: (p: number) => void;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['report-transactions', filter, page],
    queryFn: () => getTransactionReport({ ...filter, page }),
  });

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-3 bg-white border border-gray-200 rounded-lg p-4">
        <div>
          <label className="label text-xs">From</label>
          <input type="date" className="input text-sm" value={filter.start}
            onChange={(e) => onFilterChange({ ...filter, start: e.target.value })} />
        </div>
        <div>
          <label className="label text-xs">To</label>
          <input type="date" className="input text-sm" value={filter.end}
            onChange={(e) => onFilterChange({ ...filter, end: e.target.value })} />
        </div>
        <div>
          <label className="label text-xs">Type</label>
          <select className="input text-sm" value={filter.type}
            onChange={(e) => onFilterChange({ ...filter, type: e.target.value })}>
            <option value="">All</option>
            <option value="receive">Receive</option>
            <option value="issue">Issue</option>
            <option value="adjustment">Adjustment</option>
          </select>
        </div>
        <div>
          <label className="label text-xs">User</label>
          <input className="input text-sm" placeholder="Email filter" value={filter.user}
            onChange={(e) => onFilterChange({ ...filter, user: e.target.value })} />
        </div>
        <div className="flex items-end">
          <button className="btn-ghost text-xs"
            onClick={() => { onFilterChange({ start: '', end: '', type: '', user: '' }); onPageChange(1); }}>
            Clear
          </button>
        </div>
      </div>

      {isLoading && <p className="text-sm text-gray-500">Loading…</p>}

      {data && (
        <>
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {['Date','Part','Type','Qty','Before','After','By','Notes'].map(h => (
                    <th key={h} className="text-left px-4 py-2.5 text-xs font-medium text-gray-600">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.transactions.map((t) => (
                  <tr key={t.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2.5 whitespace-nowrap text-gray-600">
                      {new Date(t.performed_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-2.5 font-medium">{t.item_name}</td>
                    <td className="px-4 py-2.5">
                      <span className={`badge ${t.transaction_type === 'receive' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {t.transaction_type}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right">{t.quantity}</td>
                    <td className="px-4 py-2.5 text-right text-gray-500">{t.quantity_before}</td>
                    <td className="px-4 py-2.5 text-right">{t.quantity_after}</td>
                    <td className="px-4 py-2.5 text-gray-500 max-w-[140px] truncate">{t.performed_by ?? '—'}</td>
                    <td className="px-4 py-2.5 text-gray-500 max-w-[160px] truncate">{t.notes ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>{data.total} total records</span>
            <div className="flex gap-2">
              <button className="btn-secondary text-xs"
                disabled={page <= 1} onClick={() => onPageChange(page - 1)}>← Prev</button>
              <span className="px-2 py-1">Page {page}</span>
              <button className="btn-secondary text-xs"
                disabled={page * data.limit >= data.total} onClick={() => onPageChange(page + 1)}>Next →</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function LowStockTab() {
  const [threshold, setThreshold] = useState(5);
  const { data = [], isLoading } = useQuery({
    queryKey: ['report-low-stock', threshold],
    queryFn: () => getLowStockReport(threshold),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <label className="text-sm font-medium text-gray-700">Show items with qty ≤</label>
        <input type="number" className="input w-20 text-sm" min={0} value={threshold}
          onChange={(e) => setThreshold(parseInt(e.target.value) || 0)} />
      </div>

      {isLoading && <p className="text-sm text-gray-500">Loading…</p>}

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {data.length === 0 && !isLoading && (
          <p className="p-4 text-sm text-gray-500">No items below threshold.</p>
        )}
        {data.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {['Part','Part #','Type','Warehouse','In Stock','Unit'].map(h => (
                  <th key={h} className="text-left px-4 py-2.5 text-xs font-medium text-gray-600">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.map((item) => (
                <tr key={item.id} className={`hover:bg-gray-50 ${item.qty === 0 ? 'bg-red-50' : ''}`}>
                  <td className="px-4 py-2.5 font-medium">{item.name}</td>
                  <td className="px-4 py-2.5 text-gray-500">{item.part_number}</td>
                  <td className="px-4 py-2.5 text-gray-500">{item.item_type_name ?? '—'}</td>
                  <td className="px-4 py-2.5 text-gray-500">{item.warehouse_name ?? '—'}</td>
                  <td className="px-4 py-2.5">
                    <span className={`font-bold ${item.qty === 0 ? 'text-red-600' : 'text-yellow-600'}`}>
                      {item.qty}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-gray-500">{item.unit_of_measure}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function ValueByTypeTab() {
  const { data = [], isLoading } = useQuery({
    queryKey: ['report-value-by-type'],
    queryFn: getValueByTypeReport,
  });

  const totalValue = data.reduce((s, r) => s + r.total_value, 0);

  return (
    <div className="space-y-4">
      {isLoading && <p className="text-sm text-gray-500">Loading…</p>}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {data.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {['Item Type','# Items','Total Qty','Total Value','% of Value'].map(h => (
                  <th key={h} className="text-left px-4 py-2.5 text-xs font-medium text-gray-600">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.map((row, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-4 py-2.5 font-medium">{row.item_type}</td>
                  <td className="px-4 py-2.5 text-right">{row.item_count}</td>
                  <td className="px-4 py-2.5 text-right">{row.total_qty}</td>
                  <td className="px-4 py-2.5 text-right font-medium">${row.total_value.toFixed(2)}</td>
                  <td className="px-4 py-2.5 text-right text-gray-500">
                    {totalValue > 0 ? ((row.total_value / totalValue) * 100).toFixed(1) : 0}%
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t border-gray-200 bg-gray-50">
              <tr>
                <td className="px-4 py-2.5 font-semibold" colSpan={3}>Total</td>
                <td className="px-4 py-2.5 font-semibold text-right">${totalValue.toFixed(2)}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, color = 'text-gray-900' }: { label: string; value: number | string; color?: string }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-5">
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">{label}</p>
      <p className={`text-3xl font-bold ${color}`}>{value}</p>
    </div>
  );
}

function ActivityTable({ rows }: { rows: { id: number; transaction_type: string; item_name: string; part_number: string; quantity: number; performed_at: string }[] }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <table className="w-full text-sm">
        <tbody className="divide-y divide-gray-100">
          {rows.map((t) => (
            <tr key={t.id} className="hover:bg-gray-50">
              <td className="px-4 py-2.5">
                <span className={`badge ${t.transaction_type === 'receive' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                  {t.transaction_type}
                </span>
              </td>
              <td className="px-4 py-2.5 font-medium">{t.item_name}</td>
              <td className="px-4 py-2.5 text-gray-500">{t.part_number}</td>
              <td className="px-4 py-2.5 font-medium text-right">
                {t.transaction_type === 'issue' ? '-' : '+'}{t.quantity}
              </td>
              <td className="px-4 py-2.5 text-right text-gray-400">
                {new Date(t.performed_at).toLocaleDateString()}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
